import * as React from 'react';
import { FC } from 'react';

import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { BackArrowHistory } from 'components/navigation/back-arrow';
import { useSupportData } from 'hooks/use-support-data';

import { routes } from '../../../utils/routing';
import { ErrorView } from '../error-view';
import { LoadingView } from '../loading-view';
import { SubcategoryBreadcrumbs } from '../subcategory-breadcrumbs';
import { InnerBox, PageWrap, TextBlock } from '../support-form-v2.styled';

import { SUPPORT_CATEGORY_ID } from './const';
import { SupportV2CategoryView } from './support-v2-category-view';
import { ISubcategoryContent } from './types';

/**
 * Displays list of subcategories which link to the support form, or orders list, or customer initiated refund pages
 */
const SupportV2CategoryPage: FC = () => {
  const location = useLocation();
  const { categoryId } = useParams();
  const query = new URLSearchParams(location.search);
  const orderId = query.get('orderId') ?? '';
  // the following username and useremail will come from the Tims App only (in-app browswer)
  const username = query.get('username') ?? '';
  const useremail = query.get('useremail') ?? '';
  const fromApp = query.get('fromApp') === 'true';
  const closeOnBack = query.get('closeOnBack') === 'true';

  const { formatMessage } = useIntl();
  const { supportCategoryGroups, isSupportDataLoading } = useSupportData();
  const navigate = useNavigate();
  const isInRestaurantSurveysPage = SUPPORT_CATEGORY_ID.inRestaurantSurveys === categoryId;
  const isInRestaurantIssuesPage = SUPPORT_CATEGORY_ID.inRestaurantIssues === categoryId;
  const showContactGuestServiceText = isInRestaurantSurveysPage || isInRestaurantIssuesPage;

  if (isSupportDataLoading) {
    return <LoadingView />;
  }

  const categoryGroup = supportCategoryGroups?.find(group => group && group._id === categoryId);

  const categoryName = categoryGroup?.name?.locale ?? '';

  if (!supportCategoryGroups || !categoryGroup) {
    return <ErrorView message={formatMessage({ id: 'looksLikeWereExperiencingIssues' })} />;
  }

  const pageTitleText = formatMessage(
    { id: 'supportSubcategoryTitle' },
    {
      category: categoryName,
    }
  );

  const subcategories =
    categoryGroup.supportSubcategories?.map(subcat => ({
      _id: subcat?._id ?? '',
      name: subcat?.name?.locale ?? '',
      supportSubcategoryForwardToOrders: subcat?.supportSubcategoryForwardToOrders || false,
    })) || [];

  const breadcrumbSectionName = categoryGroup.name?.locale || '';

  const getRoute = (newQuery: string) => {
    let baseRoute = `${routes.supportForm}?${newQuery}`;
    if (!fromApp) {
      return baseRoute;
    }
    baseRoute += `&fromApp=${fromApp}`;
    if (username) {
      baseRoute += `&username=${encodeURIComponent(username)}&useremail=${encodeURIComponent(
        useremail
      )}`;
    }
    return baseRoute;
  };

  const onSelectSubcategory = (subcategory: ISubcategoryContent) => {
    const newQuery = new URLSearchParams({
      categoryId,
      subcategoryId: subcategory._id,
      ...(orderId && { orderId }),
    }).toString();

    if (subcategory.supportSubcategoryForwardToOrders) {
      return navigate(`${routes.supportOrders}?${newQuery}`);
    }

    return navigate(getRoute(newQuery));
  };

  return (
    <PageWrap>
      <InnerBox>
        <BackArrowHistory blockWebViewExitMessage={closeOnBack ? false : true} />
        <Helmet title={pageTitleText} />
        {!fromApp && (
          <SubcategoryBreadcrumbs
            currentSectionName={breadcrumbSectionName}
            data-testid={'subcategory-breadcrumbs'}
          />
        )}
        <SupportV2CategoryView
          pageTitle={pageTitleText}
          subcategories={subcategories}
          onSelectSubcategory={onSelectSubcategory}
        />
        {showContactGuestServiceText && (
          <TextBlock data-testid="contact-guest-service">
            {formatMessage({ id: 'contactGuestService' })}
          </TextBlock>
        )}
      </InnerBox>
    </PageWrap>
  );
};

export default SupportV2CategoryPage;
